import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Text, Flex, Input, Select, Button, HStack, VStack, Tooltip, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Textarea, FormControl, FormLabel, useDisclosure, useToast,
} from '@chakra-ui/react';
import api from '../api';

function safeFormatDate(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d.split('T')[0] : d;
  const [y, m, day] = s.split('-').map(Number);
  if (!y) return '';
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function DeadlineCell({ projectId, deadlineDate, deadlineType, onRefresh }) {
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ deadline_date: '', deadline_type: 'hard', change_reason: '' });
  const modal = useDisclosure();
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/deadline-history`);
      setHistory(data);
    } catch (e) {}
  }, [projectId]);

  const openModal = () => {
    setForm({
      deadline_date: deadlineDate ? deadlineDate.split('T')[0] : '',
      deadline_type: deadlineType || 'hard',
      change_reason: '',
    });
    fetchHistory();
    modal.onOpen();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/deadline`, form);
      toast({ title: 'Deadline updated', status: 'success', duration: 1500 });
      onRefresh();
      modal.onClose();
    } catch (e) {
      toast({ title: 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/deadline`, {
        deadline_date: null, deadline_type: null, change_reason: 'Deadline removed'
      });
      toast({ title: 'Deadline cleared', status: 'info', duration: 1500 });
      onRefresh();
      modal.onClose();
    } catch (e) {} finally { setSaving(false); }
  };

  // Build tooltip content
  const tooltipContent = () => {
    if (!deadlineDate && history.length === 0) return 'Click to set deadline';
    const lines = [];
    if (deadlineDate) {
      lines.push(`Current: ${safeFormatDate(deadlineDate)} (${deadlineType || 'unset'})`);
    }
    if (history.length > 0) {
      lines.push('──────────');
      history.slice(0, 8).forEach(h => {
        const d = safeFormatDate(h.deadline_date);
        const who = h.changed_by_name || 'System';
        const when = h.changed_at ? new Date(h.changed_at).toLocaleDateString('en-GB') : '';
        lines.push(`${d} (${h.deadline_type || '?'}) — ${h.change_reason || 'Changed'} — ${who} ${when}`);
      });
    }
    return lines.join('\n');
  };

  // Lazy fetch history on hover
  const [hovered, setHovered] = useState(false);
  useEffect(() => { if (hovered && projectId) fetchHistory(); }, [hovered, projectId, fetchHistory]);

  const typeColor = deadlineType === 'hard' ? 'red' : 'orange';
  const typeLabel = deadlineType === 'hard' ? 'H' : 'F';

  return (
    <>
      <Tooltip label={tooltipContent()} fontSize="9px" placement="top" hasArrow whiteSpace="pre-wrap" maxW="350px">
        <Box cursor="pointer" onClick={openModal} onMouseEnter={() => setHovered(true)}
          minH="20px" fontSize="12px" px={1} py={0.5}>
          {deadlineDate ? (
            <Flex align="center" gap={1}>
              <Text noOfLines={1} fontSize="11px">{safeFormatDate(deadlineDate)}</Text>
              <Badge fontSize="7px" colorScheme={typeColor} variant={deadlineType === 'hard' ? 'solid' : 'subtle'}>
                {typeLabel}
              </Badge>
            </Flex>
          ) : (
            <Text color="gray.400" fontSize="11px">—</Text>
          )}
        </Box>
      </Tooltip>

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Set Deadline</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <HStack w="100%" spacing={2}>
                <FormControl flex={1}>
                  <FormLabel fontSize="xs" mb={0.5}>Deadline Date</FormLabel>
                  <Input size="sm" type="date" value={form.deadline_date}
                    onChange={e => setForm(p => ({ ...p, deadline_date: e.target.value }))} />
                </FormControl>
                <FormControl flex={1}>
                  <FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
                  <Select size="sm" value={form.deadline_type}
                    onChange={e => setForm(p => ({ ...p, deadline_type: e.target.value }))}>
                    <option value="hard">Hard Deadline</option>
                    <option value="flexible">Flexible Deadline</option>
                  </Select>
                </FormControl>
              </HStack>
              {deadlineDate && (
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Reason for Change</FormLabel>
                  <Textarea size="sm" rows={2} value={form.change_reason}
                    onChange={e => setForm(p => ({ ...p, change_reason: e.target.value }))}
                    placeholder="Why is the deadline changing?" />
                </FormControl>
              )}

              {/* History */}
              {history.length > 0 && (
                <Box w="100%">
                  <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color="gray.500"
                    letterSpacing="0.06em" mb={1}>Change History</Text>
                  <VStack spacing={1} align="stretch" maxH="150px" overflowY="auto">
                    {history.map(h => (
                      <Box key={h.id} p={1.5} bg="gray.50" borderRadius="sm" fontSize="10px">
                        <Flex justify="space-between">
                          <HStack spacing={1}>
                            <Text fontWeight="600">{safeFormatDate(h.deadline_date)}</Text>
                            <Badge fontSize="7px" colorScheme={h.deadline_type === 'hard' ? 'red' : 'orange'}>
                              {h.deadline_type}
                            </Badge>
                          </HStack>
                          <Text color="gray.400">
                            {h.changed_by_name} · {h.changed_at ? new Date(h.changed_at).toLocaleDateString('en-GB') : ''}
                          </Text>
                        </Flex>
                        {h.change_reason && <Text color="gray.600" mt={0.5}>{h.change_reason}</Text>}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            {deadlineDate && (
              <Button size="sm" variant="ghost" colorScheme="red" mr="auto" onClick={handleClear}>
                Clear Deadline
              </Button>
            )}
            <Button size="sm" variant="ghost" mr={2} onClick={modal.onClose}>Cancel</Button>
            <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
              onClick={handleSave} isLoading={saving}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
